import { useEffect, useMemo, useState } from 'react';
import { CornerDownRight, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { formatCurrency } from '@/lib/formatters';
import { groupItems } from '@/lib/documentItems';
import type { CreateQuotationInvoicePayload } from '@/services/quotation.service';
import { previewTaxLines } from '@/lib/tax';
import type { OrganizationSettings, Quotation } from '@/types/table';

const TOLERANCE = 0.01;
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso: string, days: number) => new Date(new Date(iso).getTime() + days * 86400000).toISOString().slice(0, 10);

interface Props {
  quotation: Quotation;
  /** For the tax label and the GST split preview. */
  settings?: Pick<OrganizationSettings, 'taxMode' | 'taxLabel' | 'state'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateQuotationInvoicePayload) => void;
  isSubmitting?: boolean;
}

/**
 * Raise an invoice against an accepted quotation.
 *
 * Full    — the whole quotation, exactly as quoted. Only offered while nothing
 *           has been invoiced yet.
 * Partial — tick lines from the quotation and/or type lines with an amount.
 *           Amounts are before tax; the quotation's tax rate is added. The
 *           invoices on a quotation can never add up to more than it — the
 *           server enforces that, the preview here just shows it coming.
 */
export function CreateInvoiceDialog({ quotation, settings, open, onOpenChange, onSubmit, isSubmitting }: Props) {
  const currency = quotation.currency;
  const total = Number(quotation.totalAmount) || 0;
  const invoiced = Number(quotation.invoicedAmount) || 0;
  const remaining = Math.max(0, Math.round((total - invoiced) * 100) / 100);
  const fullAllowed = invoiced <= 0;
  const itemized = quotation.pricingMode !== 'LUMP_SUM';
  const taxRate = Number(quotation.taxRate) || 0;
  const lines = useMemo(() => groupItems(quotation.items), [quotation.items]);

  const [mode, setMode] = useState<'FULL' | 'PARTIAL'>(fullAllowed ? 'FULL' : 'PARTIAL');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(plusDays(today(), 30));
  const [picked, setPicked] = useState<string[]>([]);
  const [freeLines, setFreeLines] = useState<Array<{ description: string; amount: string }>>([]);
  const [discount, setDiscount] = useState('');

  // Start clean every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setMode(fullAllowed ? 'FULL' : 'PARTIAL');
    setIssueDate(today());
    setDueDate(plusDays(today(), 30));
    setPicked([]);
    setFreeLines(itemized ? [] : [{ description: '', amount: '' }]);
    setDiscount('');
  }, [open, fullAllowed, itemized]);

  const pickedSubtotal = lines
    .filter(({ item }) => picked.includes(item.id))
    .reduce((sum, { item }) => sum + (Number(item.amount) || 0), 0);
  const freeSubtotal = freeLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const subtotal = pickedSubtotal + freeSubtotal;
  const tax = subtotal * (taxRate / 100);
  const discountValue = Number(discount) || 0;
  const partialTotal = Math.max(0, subtotal + tax - discountValue);
  // Same split the server will freeze onto the invoice (it follows the quotation).
  const taxPreview = previewTaxLines(taxRate, tax, settings, quotation.taxSupply ?? 'AUTO', quotation);

  const invoiceTotal = mode === 'FULL' ? total : partialTotal;
  const overCap = invoiceTotal > remaining + TOLERANCE;
  const incompleteLine = freeLines.some((l) => !l.description.trim() || !(Number(l.amount) > 0));
  const canSubmit = mode === 'FULL'
    ? fullAllowed
    : (picked.length > 0 || freeLines.length > 0) && !incompleteLine && partialTotal > 0 && !overCap;

  const submit = () => {
    if (!canSubmit) return;
    const base = { issueDate, dueDate };
    if (mode === 'FULL') { onSubmit({ mode, ...base }); return; }
    onSubmit({
      mode,
      ...base,
      quotationItemIds: picked.length ? picked : undefined,
      lines: freeLines.length ? freeLines.map((l) => ({ description: l.description.trim(), amount: Number(l.amount) })) : undefined,
      discountAmount: discountValue > 0 ? discountValue : undefined,
    });
  };

  const money = (v: number) => formatCurrency(v, currency);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create invoice</DialogTitle>
          <DialogDescription>
            {quotation.quotationNumber}: {money(total)} quoted · {money(invoiced)} invoiced · <span className="font-medium text-foreground">{money(remaining)} left to invoice</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'FULL' | 'PARTIAL')} className="grid gap-2 sm:grid-cols-2">
            <Label htmlFor="ci-full" className={`flex cursor-pointer gap-3 rounded-md border p-3 font-normal ${mode === 'FULL' ? 'border-primary' : ''} ${fullAllowed ? '' : 'cursor-not-allowed opacity-50'}`}>
              <RadioGroupItem value="FULL" id="ci-full" disabled={!fullAllowed} className="mt-0.5" />
              <span>
                <span className="block font-medium">Full</span>
                <span className="text-xs text-muted-foreground">
                  {fullAllowed ? `Every line, tax and discount as quoted — ${money(total)}` : 'Part of this quotation is already invoiced'}
                </span>
              </span>
            </Label>
            <Label htmlFor="ci-partial" className={`flex cursor-pointer gap-3 rounded-md border p-3 font-normal ${mode === 'PARTIAL' ? 'border-primary' : ''}`}>
              <RadioGroupItem value="PARTIAL" id="ci-partial" className="mt-0.5" />
              <span>
                <span className="block font-medium">Partial</span>
                <span className="text-xs text-muted-foreground">Pick lines and/or add lines with an amount — a stage, a month, an advance</span>
              </span>
            </Label>
          </RadioGroup>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label htmlFor="ci-issue">Issue date</Label><Input id="ci-issue" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></div>
            <div className="space-y-1"><Label htmlFor="ci-due">Due date</Label><Input id="ci-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
          </div>

          {mode === 'PARTIAL' && (
            <div className="space-y-4">
              {itemized && lines.length > 0 && (
                <div className="space-y-2">
                  <Label>Lines from the quotation</Label>
                  <div className="divide-y rounded-md border">
                    {lines.map(({ item, children }) => {
                      const checked = picked.includes(item.id);
                      return (
                        <label key={item.id} className="flex cursor-pointer gap-3 p-3 text-sm hover:bg-muted/50">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => setPicked((prev) => (v ? [...prev, item.id] : prev.filter((x) => x !== item.id)))}
                            className="mt-0.5"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block">{item.description}</span>
                            {children.map((c) => (
                              <span key={c.id} className="flex items-center gap-1 text-xs text-muted-foreground"><CornerDownRight className="h-3 w-3" />{c.description}</span>
                            ))}
                          </span>
                          <span className="whitespace-nowrap tabular-nums">{money(Number(item.amount))}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{itemized ? 'Other lines' : 'Lines'}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setFreeLines((prev) => [...prev, { description: '', amount: '' }])}>
                    <Plus className="mr-1 h-4 w-4" />Add line
                  </Button>
                </div>
                {!itemized && <p className="text-xs text-muted-foreground">This quotation is one lump sum, so describe what this invoice covers and its amount.</p>}
                {freeLines.map((line, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      placeholder="e.g. Milestone 1 — design sign-off"
                      value={line.description}
                      onChange={(e) => setFreeLines((prev) => prev.map((l, j) => (j === i ? { ...l, description: e.target.value } : l)))}
                    />
                    <DecimalInput
                      className="w-36"
                      placeholder="Amount"
                      value={line.amount}
                      onChange={(e) => setFreeLines((prev) => prev.map((l, j) => (j === i ? { ...l, amount: e.target.value } : l)))}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => setFreeLines((prev) => prev.filter((_, j) => j !== i))} aria-label="Remove line">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">Amounts are before tax{taxRate > 0 ? ` — ${taxRate}% is added, as on the quotation` : ''}.</p>
              </div>

              <div className="ml-auto w-full max-w-xs space-y-1 border-t pt-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(subtotal)}</span></div>
                {taxPreview.map((line) => (
                  <div key={line.label} className="flex justify-between"><span className="text-muted-foreground">{line.label}</span><span>{money(line.amount)}</span></div>
                ))}
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="ci-discount" className="font-normal text-muted-foreground">Discount (optional)</Label>
                  <DecimalInput id="ci-discount" className="h-8 w-28 text-right" placeholder="0.00" value={discount} onChange={(e) => setDiscount(e.target.value)} />
                </div>
                <div className="flex justify-between border-t pt-1 font-bold"><span>Invoice total</span><span>{money(partialTotal)}</span></div>
              </div>
            </div>
          )}

          {overCap && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              This invoice comes to {money(invoiceTotal)}, but only {money(remaining)} of the quotation is left. Anything beyond the quotation needs a new quotation or a separate invoice.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={!canSubmit} loading={isSubmitting}>Create draft invoice</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
